import type { ClientPushCategoryUserSetting } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, type FC } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { PushCategoryIconRecord } from '@/features/profile-setting/constants/pushCategories';
import {
  getPushCategoriesQueryKey,
  setPushCategoryEnabled
} from '@/features/profile-setting/utils/notificationSettingCache';
import { AppSwitch } from '@/shared/components/AppSwitch';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const REQUEST_DEBOUNCE_MS = 150;

type Props = {
  category: ClientPushCategoryUserSetting;
  onChange: (id: string, enabled: boolean) => void;
};

export const CategoryItem: FC<Props> = ({ category, onChange }) => {
  const theme = useAppTheme();
  const queryClient = useQueryClient();

  const { id, name, loudPushEnabled } = category;

  const changeSetting = useDebouncedCallback(
    (enabled: boolean) => onChange(id, enabled),
    REQUEST_DEBOUNCE_MS
  );

  const handleChange = useCallback(
    (enabled: boolean) => {
      void queryClient.cancelQueries({ queryKey: getPushCategoriesQueryKey() });
      setPushCategoryEnabled(queryClient, id, enabled);

      changeSetting(enabled);
    },
    [changeSetting, id, queryClient]
  );

  return (
    <AppSwitch
      title={name}
      icon={
        PushCategoryIconRecord[id]
          ? () =>
              PushCategoryIconRecord[id]({
                fill: theme.colors.onSurface,
                width: 24,
                height: 24
              })
          : undefined
      }
      value={loudPushEnabled}
      onValueChange={handleChange}
      backgroundColor={theme.colors.surfaceContainerLowest}
    />
  );
};
